import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /ltr — TH hub for Long-Term Resident Visa (BOI 10-year)
// Phase P-LTR-VISA
import { createFileRoute, Link } from "@tanstack/react-router";
import { LTR_SEGMENTS } from "@/data/ltr-segments";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, Award, Wallet, CalendarClock, FileText, ShieldCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/ltr/")({
  head: () => {
    const url = `${SITE}/ltr`;
    const title = `LTR Visa — Long-Term Resident วีซ่า 10 ปี BOI Thailand 2026 | NYC`;
    const desc = `Long-Term Resident Visa 10 ปี (5+5) จาก BOI Thailand — Wealthy Global Citizen, Pensioner, Work-from-Thailand, Highly-Skilled (ภาษี 17%), Dependent. ครบทุกขั้นตอน เอกสาร และค่าธรรมเนียม — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    return {
      meta: [
        { title: ctaTitle(title, "/ltr", "th") }, { name: "description", content: ctaDesc(desc, "/ltr", "th")},
        { property: "og:title", content: ctaTitle(title, "/ltr", "th")}, { property: "og:description", content: ctaDesc(desc, "/ltr", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "LTR visa Thailand, Long-Term Resident, BOI 10-year visa, wealthy pensioner Thailand, highly skilled professional 17%, work from Thailand visa" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/ltr", "/en/ltr")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "th-TH",
              mainEntity: {
                "@type": "ItemList", numberOfItems: LTR_SEGMENTS.length,
                itemListElement: LTR_SEGMENTS.map((s, i) => ({
                  "@type": "ListItem", position: i + 1, name: s.titleTh, url: `${SITE}/ltr/${s.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "LTR Visa Application Service",
              serviceType: "Long-Term Resident Visa Application Service (BOI Thailand)",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "39000", priceCurrency: "THB", description: "LTR end-to-end: qualification check + BOI submission + visa stamp + Digital Work Permit" },
            }),
          },
          faqPageSchema([
            { q: "LTR คืออะไร?", a: "Long-Term Resident Visa — วีซ่า 10 ปี (5+5) จาก BOI ออกตั้งแต่ ก.ย. 2565 ค่าธรรมเนียม 50,000 บาท สิทธิ Digital Work Permit + รายงาน 90 วันปีละครั้ง + ภาษี foreign income ยกเว้น" },
            { q: "LTR ต่างจาก DTV อย่างไร?", a: "LTR สำหรับ HNWI / Pensioner / Senior remote professional / ผู้เชี่ยวชาญ — รายได้ ≥ USD 80K/ปี อายุ 10 ปี ส่วน DTV สำหรับ digital nomad ทั่วไป รายได้ขั้นต่ำต่ำกว่า อายุ 5 ปี" },
            { q: "HSP ได้ภาษีกี่เปอร์เซ็นต์?", a: "Highly-Skilled Professional เลือกเสียภาษีเงินได้บุคคลธรรมดาแบบ flat 17% (แทนอัตราก้าวหน้าสูงสุด 35% ตาม ป.รัษฎากร ม.48) ตาม พ.ร.ฎ.743/2565 ม.5 และประกาศอธิบดี ปท.10/2566 — ต้องทำงานใน Target Industry ตามประกาศ BOI ส.1/2565 มีเงินเดือน ≥80,000 USD/ปี ยื่น ภ.ง.ด.95 ภายใน 31 มี.ค. ของปีถัดไป" },
            { q: "ต้องอยู่ไทยกี่วันต่อปี?", a: "LTR ไม่มี minimum stay — ออกนอกประเทศได้ไม่จำกัด multiple entry ตลอดอายุวีซ่า 10 ปี ตามประกาศ ตม. 177/2565 ข้อ 4 ไม่ต้อง re-entry permit ไม่ต้องรายงานตัว 90 วันที่ ตม. (ตาม พ.ร.บ.คนเข้าเมือง 2522 ม.37(5) ยกเว้น) แต่หากอยู่ไทย ≥180 วัน/ปี จะเป็น Thai Tax Resident ตาม ป.รัษฎากร ม.41" },
            { q: "พาครอบครัวมาด้วยได้ไหม?", a: "ได้ — LTR Dependent สำหรับคู่สมรสที่จดทะเบียนถูกต้องและบุตรอายุ ≤20 ปี สูงสุด 4 คน/principal คนละ 50,000 บาท ตามประกาศ ตม. 177/2565 ข้อ 7 ใช้เอกสารสมรส/สูติบัตรผ่าน Apostille (Hague 15 ธ.ค. 2025) หรือสถานทูตปลายทาง พร้อมแปลโดยนักแปลขึ้นทะเบียน MOJ + รับรอง MFA — NYC จัดเตรียมครบ 5–7 วัน" },
          ]),
        ]),
      ],
    };
  },
  component: LtrHubTh,
});

function LtrHubTh() {
  return (
    <main className="min-h-screen bg-background">
      <AnswerBoxTop cluster="ltr" locale="th" />
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link><span className="mx-2">/</span><span>LTR Visa</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Award className="mr-1 inline h-3 w-3" /> BOI Long-Term Resident · 5 segments
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">LTR Visa — Long-Term Resident 10 ปี</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            วีซ่าระดับพรีเมียมของ BOI Thailand — 10 ปี (5+5), Digital Work Permit, ภาษี foreign income ยกเว้น และ HSP ได้ flat 17% สำหรับ HNWI, ผู้เกษียณ, remote professional ระดับสูง, ผู้เชี่ยวชาญ และครอบครัว
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">LTR Visa คืออะไร? เหมาะกับใคร?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Long-Term Resident เป็นวีซ่า 10 ปีของ BOI Thailand ออกตั้งแต่ ก.ย. 2565 มี 4 กลุ่มหลัก: Wealthy Global Citizen (สินทรัพย์ ≥ USD 1M + ลงทุน USD 500K), Wealthy Pensioner (50+ pension ≥ USD 80K), Work-from-Thailand (senior remote ≥ USD 80K), Highly-Skilled Professional (ภาษี flat 17%) + Dependent. ค่าธรรมเนียม BOI 50,000 บาท/คน รวม Digital Work Permit + รายงาน 90 วันแค่ปีละครั้ง + ภาษี foreign income ยกเว้นตาม Royal Decree 743
            </p>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-4 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3"><CalendarClock className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">อายุวีซ่า</div><div className="text-sm font-bold">10 ปี (5+5)</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Wallet className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">ค่า BOI</div><div className="text-sm font-bold">50,000 บาท</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><FileText className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">90-day report</div><div className="text-sm font-bold">ปีละครั้ง</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><ShieldCheck className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Work Permit</div><div className="text-sm font-bold">Digital ฟรี</div></div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/dtv" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <FileText className="h-4 w-4" /> เทียบกับ DTV 5 ปี
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">5 กลุ่มของ LTR Visa</h2>
          <p className="mt-2 text-sm text-muted-foreground">เลือกกลุ่มที่ตรงกับ profile ของคุณ · คลิกเพื่อดูเงื่อนไข เอกสาร และค่าใช้จ่าย</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {LTR_SEGMENTS.map((s) => (
              <Link
                key={s.slug}
                to="/ltr/$segment"
                params={{ segment: s.slug }}
                className="group rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                data-track-zone="ltr-hub-th"
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{s.icon}</span>
                  <div className="font-bold text-sm">{s.titleTh}</div>
                </div>
                <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{s.taglineTh}</div>
                <div className="mt-3 text-[11px] font-semibold text-foreground/80">{s.qualifyTh}</div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">฿{s.servicePriceTHB.toLocaleString()}</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary">
                    ดู <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold">บริการที่เกี่ยวข้อง</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Visa Matrix — เทียบ 7 วีซ่า", d: "DTV vs LTR vs Non-O ใน 60 วินาที", to: "/visa-matrix" as const },
              { t: "DTV 5 ปี", d: "ทางเลือกสำหรับ Digital Nomad", to: "/dtv" as const },
              { t: "วีซ่าทุกประเภท", d: "Non-B / Retirement / Marriage", to: "/visa" as const },
              { t: "Visa Compliance", d: "TM30 / 90-day / Re-entry", to: "/visa-compliance" as const },
              { t: "Work Permit", d: "สำหรับงานในบริษัทไทย", to: "/work-permit" as const },
              { t: "แลกใบขับขี่", d: "DLT 5 ปี ด้วย LTR / Cert Residence", to: "/driving-license" as const },
              { t: "MFA + Apostille", d: "รับรองเอกสารต่างประเทศ", to: "/mfa-legalisation" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    
      <PlanningToolsLinks locale="th" />
      </main>
  );
}
